import React from "react";
import { observer } from "mobx-react";
import ReactApexChart from "react-apexcharts";
import {
  baseChartOptions,
  axisLabelStyle,
  seriesColors,
  axisFormatter
} from "../visuals/chartTheme";
import { getNiceMax } from "Pages/Dashboard/dashboard/DashboardV2/utils/chartDataUtils";
import { formatMetric } from "../visuals/formatMetric";
import { useTokenHost } from "../visuals/cssVar";

/**
 * Trend over time.
 *
 * Two rules the reference dashboards follow and Apex does not by default:
 *
 * 1. A SECOND CUE beyond colour. Each series gets its own dash pattern and
 *    marker shape, and the legend says so ("Sessions (dashed)"). Colour alone
 *    fails for colour-blind readers and in greyscale print.
 * 2. ONE y-axis. Two scales in one frame invite a false correlation, so a
 *    second unit is refused rather than silently given its own axis.
 */
const DASH = [0, 6, 2, 8];
const MARKERS = ["circle", "square", "triangle", "diamond"];

const LineChartBlock = observer(({ block }) => {
  // `ready` forces one re-render after mount so token reads see a live ref.
  const [rootRef, ready] = useTokenHost();
  const series = (block.series || []).filter(s => s && (s.data || []).length);
  if (!series.length) return null;

  const unit = block.unit || "";
  const palette = seriesColors(rootRef.current, Math.max(series.length, 2));
  const base = baseChartOptions(rootRef.current);
  const multi = series.length > 1;

  // Same axis rounding the dashboard widgets use, so a metric charted in the
  // chat lands on the same bounds as the reports page.
  const peak = Math.max(...series.flatMap(s => (s.data || []).map(Number).filter(Number.isFinite)), 1);
  const { max: niceMax, tickAmount } = getNiceMax(peak);

  const options = {
    ...base,
    chart: { ...base.chart, type: "line" },
    colors: palette,
    stroke: {
      curve: "smooth",
      width: 2,
      dashArray: series.map((unused, i) => DASH[i % DASH.length])
    },
    markers: {
      size: 5,
      strokeWidth: 2,
      strokeColors: "#fff",
      shape: series.map((unused, i) => MARKERS[i % MARKERS.length]),
      hover: { size: 7 }
    },
    // A legend for one series is noise — the title already names it.
    legend: multi
      ? {
          show: true,
          position: "top",
          horizontalAlign: "left",
          fontSize: "12px",
          markers: { radius: 3 },
          itemMargin: { horizontal: 12 }
        }
      : { show: false },
    xaxis: {
      categories: block.categories || [],
      labels: axisLabelStyle(rootRef.current),
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false }
    },
    yaxis: {
      labels: { ...axisLabelStyle(rootRef.current), formatter: axisFormatter(unit) },
      min: 0,
      max: niceMax,
      tickAmount
    },
    tooltip: { ...base.tooltip, shared: true, y: { formatter: value => formatMetric(value, { unit }) } }
  };

  // The dash pattern is meaningless unless the legend names it.
  const named = series.map((s, i) => ({
    name: multi ? `${s.name}${i === 0 ? " (solid)" : " (dashed)"}` : s.name,
    data: s.data
  }));

  return (
    <div className="cpVisual" ref={rootRef}>
      {block.title && <div className="cpVisual__title">{block.title}</div>}
      <ReactApexChart key={ready ? "themed" : "initial"} options={options} series={named} type="line" height={280} />
    </div>
  );
});

export default LineChartBlock;
